/**
 * Amazon-Style Collection Filters
 * Handles variant filtering, price range, availability, sorting, and more
 */

class FilteredCollectionGallery {
  constructor() {
    this.collectionHandle = document.querySelector('[data-collection-handle]')?.dataset.collectionHandle || '';
    this.productsPerRow = window.innerWidth < 640 ? 2 : window.innerWidth < 1024 ? 3 : window.innerWidth < 1280 ? 4 : 5;
    this.rowsToShow = 5;
    this.productsPerLoad = this.productsPerRow * this.rowsToShow;
    this.allProducts = [];
    this.filteredProducts = [];
    this.currentPage = 0;
    this.isLoading = false;
    
    // Filter state
    this.activeFilters = {
      variants: {}, // { "Color": ["Red", "Blue"], "Size": ["M", "L"] }
      price: { min: null, max: null },
      availability: false
    };
    
    this.sortBy = 'default';
    
    this.init();
  }

  async init() {
    await this.fetchProducts();
    this.filteredProducts = [...this.allProducts]; // Initialize with all products
    this.buildFilters();
    this.updateResultsCount();
    this.loadProducts();
    this.setupEventListeners();
    this.setupInfiniteScroll();
    
    window.addEventListener('resize', () => {
      this.productsPerRow = window.innerWidth < 640 ? 2 : window.innerWidth < 1024 ? 3 : window.innerWidth < 1280 ? 4 : 5;
      this.productsPerLoad = this.productsPerRow * this.rowsToShow;
    });
  }

  async fetchProducts() {
    try {
      const response = await fetch(`/collections/${this.collectionHandle}/products.json?limit=250`);
      const data = await response.json();
      this.allProducts = data.products;
      console.log('✅ Loaded', this.allProducts.length, 'products');
    } catch (error) {
      console.error('❌ Error fetching products:', error);
    }
  }

  // Build filter sidebar from product data
  buildFilters() {
    const filterGroups = document.getElementById('filter-groups');
    if (!filterGroups) return;

    // Extract all unique variant options
    const variantOptions = {};
    const prices = [];

    this.allProducts.forEach(product => {
      // Collect prices
      product.variants.forEach(v => {
        prices.push(parseFloat(v.price));
      });

      // Collect variant options
      product.options?.forEach(option => {
        if (!variantOptions[option.name]) {
          variantOptions[option.name] = new Set();
        }
        option.values?.forEach(value => {
          variantOptions[option.name].add(value);
        });
      });
    });

    // Build variant filters (exclude Title and other non-filterable options)
    const excludedOptions = ['title', 'default title', 'default'];
    
    Object.entries(variantOptions).forEach(([optionName, values]) => {
      // Skip if it's a title-related option
      if (excludedOptions.includes(optionName.toLowerCase())) {
        console.log('⏭️ Skipping filter:', optionName);
        return;
      }
      
      const isColor = optionName.toLowerCase() === 'color' || optionName.toLowerCase() === 'colour';
      this.createFilterGroup(filterGroups, optionName, Array.from(values), isColor);
    });

    // Build price filter
    if (prices.length > 0) {
      const minPrice = Math.floor(Math.min(...prices));
      const maxPrice = Math.ceil(Math.max(...prices));
      this.createPriceFilter(filterGroups, minPrice, maxPrice);
    }

    // Build availability filter
    this.createAvailabilityFilter(filterGroups);
  }

  createFilterGroup(container, name, values, isColor = false) {
    const group = document.createElement('div');
    group.className = 'filter-group';
    group.dataset.filterType = name;

    const title = document.createElement('div');
    title.className = 'filter-group-title';
    title.innerHTML = `${name} <span class="count"></span>`;
    title.onclick = () => group.classList.toggle('collapsed');

    const content = document.createElement('div');
    content.className = 'filter-group-content';

    if (isColor) {
      const swatches = document.createElement('div');
      swatches.className = 'color-swatches';
      values.forEach(value => {
        const swatch = this.createColorSwatch(name, value);
        swatches.appendChild(swatch);
      });
      content.appendChild(swatches);
    } else {
      values.forEach(value => {
        const option = this.createFilterOption(name, value);
        content.appendChild(option);
      });
    }

    group.appendChild(title);
    group.appendChild(content);
    container.appendChild(group);
  }

  createColorSwatch(optionName, value) {
    const swatch = document.createElement('div');
    swatch.className = 'color-swatch';
    swatch.title = value;
    swatch.style.background = this.getColorCode(value);
    swatch.dataset.option = optionName;
    swatch.dataset.value = value;
    
    swatch.onclick = () => {
      swatch.classList.toggle('selected');
      this.toggleFilter(optionName, value);
    };

    return swatch;
  }

  createFilterOption(optionName, value) {
    const option = document.createElement('div');
    option.className = 'filter-option';

    const checkbox = document.createElement('div');
    checkbox.className = 'filter-checkbox';
    checkbox.dataset.option = optionName;
    checkbox.dataset.value = value;

    const label = document.createElement('span');
    label.className = 'filter-label';
    label.textContent = value;

    const count = document.createElement('span');
    count.className = 'filter-count';
    count.textContent = `(${this.getProductCountForFilter(optionName, value)})`;

    option.appendChild(checkbox);
    option.appendChild(label);
    option.appendChild(count);

    option.onclick = () => {
      checkbox.classList.toggle('checked');
      this.toggleFilter(optionName, value);
    };

    return option;
  }

  createPriceFilter(container, min, max) {
    const group = document.createElement('div');
    group.className = 'filter-group';

    const title = document.createElement('div');
    title.className = 'filter-group-title';
    title.textContent = 'Price';
    title.onclick = () => group.classList.toggle('collapsed');

    const content = document.createElement('div');
    content.className = 'filter-group-content';

    content.innerHTML = `
      <div class="price-range-inputs">
        <input type="number" class="price-input" id="price-min" placeholder="£${min}" min="${min}" max="${max}">
        <span class="price-separator">to</span>
        <input type="number" class="price-input" id="price-max" placeholder="£${max}" min="${min}" max="${max}">
      </div>
      <button class="apply-price-btn">Go</button>
    `;

    const btn = content.querySelector('.apply-price-btn');
    btn.onclick = () => {
      const minInput = document.getElementById('price-min');
      const maxInput = document.getElementById('price-max');
      this.activeFilters.price.min = parseFloat(minInput.value) || null;
      this.activeFilters.price.max = parseFloat(maxInput.value) || null;
      this.applyFilters();
    };

    group.appendChild(title);
    group.appendChild(content);
    container.appendChild(group);
  }

  createAvailabilityFilter(container) {
    const group = document.createElement('div');
    group.className = 'filter-group';

    const title = document.createElement('div');
    title.className = 'filter-group-title';
    title.textContent = 'Availability';
    title.onclick = () => group.classList.toggle('collapsed');

    const content = document.createElement('div');
    content.className = 'filter-group-content';

    const option = document.createElement('div');
    option.className = 'filter-option';

    const checkbox = document.createElement('div');
    checkbox.className = 'filter-checkbox';
    checkbox.id = 'availability-checkbox';

    const label = document.createElement('span');
    label.className = 'filter-label';
    label.textContent = 'In stock only';

    option.appendChild(checkbox);
    option.appendChild(label);

    option.onclick = () => {
      checkbox.classList.toggle('checked');
      this.activeFilters.availability = checkbox.classList.contains('checked');
      this.applyFilters();
    };

    content.appendChild(option);
    group.appendChild(title);
    group.appendChild(content);
    container.appendChild(group);
  }

  getColorCode(colorName) {
    const colors = {
      'red': '#e74c3c',
      'blue': '#3498db',
      'green': '#2ecc71',
      'yellow': '#f1c40f',
      'black': '#2c3e50',
      'white': '#ecf0f1',
      'purple': '#9b59b6',
      'pink': '#e91e63',
      'orange': '#ff9800',
      'brown': '#795548',
      'gray': '#9e9e9e',
      'grey': '#9e9e9e',
      'beige': '#d7ccc8',
      'navy': '#001f3f',
      'teal': '#009688',
      'silver': '#c0c0c0',
      'gold': '#ffd700',
      'maroon': '#800000',
      'olive': '#808000'
    };
    return colors[colorName.toLowerCase()] || '#ccc';
  }

  getProductCountForFilter(optionName, value) {
    return this.allProducts.filter(product => {
      return product.options?.some(opt => 
        opt.name === optionName && opt.values?.includes(value)
      );
    }).length;
  }

  toggleFilter(optionName, value) {
    if (!this.activeFilters.variants[optionName]) {
      this.activeFilters.variants[optionName] = [];
    }

    const index = this.activeFilters.variants[optionName].indexOf(value);
    if (index > -1) {
      this.activeFilters.variants[optionName].splice(index, 1);
      if (this.activeFilters.variants[optionName].length === 0) {
        delete this.activeFilters.variants[optionName];
      }
    } else {
      this.activeFilters.variants[optionName].push(value);
    }

    this.applyFilters();
  }

  applyFilters() {
    this.filteredProducts = this.allProducts.filter(product => {
      // Variant filters (OR logic within same option, AND between different options)
      for (const [optionName, values] of Object.entries(this.activeFilters.variants)) {
        const hasMatchingVariant = product.options?.some(opt => {
          if (opt.name === optionName) {
            return values.some(val => opt.values?.includes(val));
          }
          return false;
        });
        if (!hasMatchingVariant) return false;
      }

      // Price filter
      const { min, max } = this.activeFilters.price;
      if (min !== null || max !== null) {
        const productPrice = parseFloat(product.variants[0].price);
        if (min !== null && productPrice < min) return false;
        if (max !== null && productPrice > max) return false;
      }

      // Availability filter
      if (this.activeFilters.availability) {
        if (!product.variants.some(v => v.available)) return false;
      }

      return true;
    });

    // Apply sorting
    this.sortProducts();

    // Reset pagination
    this.currentPage = 0;

    // Update UI
    this.updateActiveFiltersUI();
    this.updateResultsCount();
    this.loadProducts(false);
  }

  sortProducts() {
    switch (this.sortBy) {
      case 'price-asc':
        this.filteredProducts.sort((a, b) => parseFloat(a.variants[0].price) - parseFloat(b.variants[0].price));
        break;
      case 'price-desc':
        this.filteredProducts.sort((a, b) => parseFloat(b.variants[0].price) - parseFloat(a.variants[0].price));
        break;
      case 'title-asc':
        this.filteredProducts.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-desc':
        this.filteredProducts.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'created-desc':
        this.filteredProducts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      default:
        // Keep default order
        break;
    }
  }

  updateActiveFiltersUI() {
    const container = document.getElementById('active-filters');
    const clearBtn = document.getElementById('clear-all-filters');
    
    if (!container) return;

    container.innerHTML = '';

    let hasFilters = false;

    // Variant filters
    for (const [optionName, values] of Object.entries(this.activeFilters.variants)) {
      values.forEach(value => {
        hasFilters = true;
        const chip = document.createElement('div');
        chip.className = 'filter-chip';
        chip.innerHTML = `
          ${optionName}: ${value}
          <span class="filter-chip-remove">×</span>
        `;
        chip.querySelector('.filter-chip-remove').onclick = (e) => {
          e.stopPropagation();
          this.toggleFilter(optionName, value);
          this.updateCheckboxState(optionName, value, false);
        };
        container.appendChild(chip);
      });
    }

    // Price filter
    if (this.activeFilters.price.min !== null || this.activeFilters.price.max !== null) {
      hasFilters = true;
      const { min, max } = this.activeFilters.price;
      const priceText = `£${min || '0'} - £${max || '∞'}`;
      const chip = document.createElement('div');
      chip.className = 'filter-chip';
      chip.innerHTML = `
        ${priceText}
        <span class="filter-chip-remove">×</span>
      `;
      chip.querySelector('.filter-chip-remove').onclick = () => {
        this.activeFilters.price = { min: null, max: null };
        const minInput = document.getElementById('price-min');
        const maxInput = document.getElementById('price-max');
        if (minInput) minInput.value = '';
        if (maxInput) maxInput.value = '';
        this.applyFilters();
      };
      container.appendChild(chip);
    }

    // Availability filter
    if (this.activeFilters.availability) {
      hasFilters = true;
      const chip = document.createElement('div');
      chip.className = 'filter-chip';
      chip.innerHTML = `
        In stock only
        <span class="filter-chip-remove">×</span>
      `;
      chip.querySelector('.filter-chip-remove').onclick = () => {
        this.activeFilters.availability = false;
        const checkbox = document.getElementById('availability-checkbox');
        if (checkbox) checkbox.classList.remove('checked');
        this.applyFilters();
      };
      container.appendChild(chip);
    }

    if (clearBtn) {
      clearBtn.style.display = hasFilters ? 'block' : 'none';
    }
  }

  updateCheckboxState(optionName, value, checked) {
    // Update checkbox UI
    const checkboxes = document.querySelectorAll('.filter-checkbox');
    checkboxes.forEach(cb => {
      if (cb.dataset.option === optionName && cb.dataset.value === value) {
        if (checked) {
          cb.classList.add('checked');
        } else {
          cb.classList.remove('checked');
        }
      }
    });

    // Update color swatch UI
    const swatches = document.querySelectorAll('.color-swatch');
    swatches.forEach(swatch => {
      if (swatch.dataset.option === optionName && swatch.dataset.value === value) {
        if (checked) {
          swatch.classList.add('selected');
        } else {
          swatch.classList.remove('selected');
        }
      }
    });
  }

  updateResultsCount() {
    const countEl = document.getElementById('results-count');
    if (countEl) {
      const total = this.filteredProducts.length;
      countEl.textContent = `${total} ${total === 1 ? 'result' : 'results'}`;
    }
  }

  setupEventListeners() {
    // Clear all filters
    const clearBtn = document.getElementById('clear-all-filters');
    if (clearBtn) {
      clearBtn.onclick = () => {
        this.activeFilters = {
          variants: {},
          price: { min: null, max: null },
          availability: false
        };
        
        // Reset UI
        document.querySelectorAll('.filter-checkbox').forEach(cb => cb.classList.remove('checked'));
        document.querySelectorAll('.color-swatch').forEach(sw => sw.classList.remove('selected'));
        const minInput = document.getElementById('price-min');
        const maxInput = document.getElementById('price-max');
        if (minInput) minInput.value = '';
        if (maxInput) maxInput.value = '';
        
        this.applyFilters();
      };
    }

    // Sort dropdown
    const sortSelect = document.getElementById('sort-select');
    if (sortSelect) {
      sortSelect.onchange = (e) => {
        this.sortBy = e.target.value;
        this.applyFilters();
      };
    }
  }

  loadProducts(append = false) {
    const grid = document.getElementById('collection-products-grid');
    if (!grid) return;

    const products = this.filteredProducts;
    const start = this.currentPage * this.productsPerLoad;
    const end = start + this.productsPerLoad;
    const productsToShow = products.slice(start, end);

    if (productsToShow.length === 0 && !append) {
      grid.innerHTML = '<div class="no-products">No products match your filters</div>';
      return;
    }

    const productHTML = productsToShow.map(product => this.createProductCard(product)).join('');
    
    if (append) {
      grid.insertAdjacentHTML('beforeend', productHTML);
    } else {
      grid.innerHTML = productHTML;
    }
    
    this.currentPage++;
    this.isLoading = false;
  }

  createProductCard(product) {
    const price = parseFloat(product.variants[0].price).toFixed(2);
    const comparePrice = product.variants[0].compare_at_price ? parseFloat(product.variants[0].compare_at_price).toFixed(2) : null;
    const image = product.images && product.images.length > 0 ? product.images[0] : product.featured_image;
    const imageUrl = image ? (typeof image === 'string' ? image : image.src || image) : '';

    return `
      <div class="product-card" data-product-handle="${product.handle}">
        <div class="product-image-wrapper" onclick="window.location.href='/products/${product.handle}'">
          ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" class="product-image" loading="lazy">` : '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#999;">No Image</div>'}
        </div>
        <div class="product-info" onclick="window.location.href='/products/${product.handle}'">
          <h3 class="product-title">
            <a href="/products/${product.handle}">${product.title}</a>
          </h3>
          <div class="product-price-wrapper">
            <span class="product-price">£${price}</span>
            ${comparePrice ? `<span class="product-compare-price">£${comparePrice}</span>` : ''}
          </div>
        </div>
        <button class="add-to-cart-btn startup-quickview-btn quickview-trigger" 
                type="button"
                data-open-quickview
                data-quickview-handle="${product.handle}"
                data-product-id="${product.id}"
                data-product-handle="${product.handle}"
                data-product-url="/products/${product.handle}"
                aria-label="Quick view ${product.title}">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
        </button>
      </div>
    `;
  }

  setupInfiniteScroll() {
    window.addEventListener('scroll', () => {
      if (this.isLoading) return;
      
      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.scrollHeight - 500;

      if (scrollPosition >= threshold) {
        const totalLoaded = this.currentPage * this.productsPerLoad;

        if (totalLoaded < this.filteredProducts.length) {
          this.isLoading = true;
          const spinner = document.getElementById('loading-spinner');
          if (spinner) spinner.classList.add('active');

          setTimeout(() => {
            this.loadProducts(true);
            if (spinner) spinner.classList.remove('active');
          }, 500);
        }
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.getElementById('filter-groups')) {
      window.collectionGallery = new FilteredCollectionGallery();
    }
  });
} else {
  if (document.getElementById('filter-groups')) {
    window.collectionGallery = new FilteredCollectionGallery();
  }
}
